/**
 * Pure helpers for checkout segment state and settlement branching.
 * The Pinia store imports these; UI should not call them directly.
 *
 * Each **segment** is one step in the checkout API sequence (prepare-cart, check-for-credits,
 * finalize, place, coupon). Steps may run independently or via the phased checkout pipeline.
 */

import { toRaw } from 'vue'
import type {
    CheckoutEntitlementsData,
    CheckoutJourney,
    CheckoutSegment,
    CheckoutSegmentStatus,
    CheckoutSettlement,
    CheckoutUiMode,
} from '~~/layers/base/types/checkout'
import { isCheckoutApiJourney } from '~~/layers/base/utils/checkoutJourneyPolicy'

function cloneSkeleton<T>(skeleton: T): T {
    return structuredClone(toRaw(skeleton))
}

/** New segment in `idle` with `data` cloned from `skeleton`. */
export function createIdleSegment<T>(skeleton: T): CheckoutSegment<T> {
    return {
        data: cloneSkeleton(skeleton),
        skeleton,
        status: 'idle',
    }
}

/**
 * New segment in `loading` with `data` cloned from `skeleton`.
 * Used for checkout **addresses** so Recipient / Return stacks skeleton on first paint
 * instead of rendering empty labels while still `idle` (Send → `/checkout`).
 */
export function createLoadingSegment<T>(skeleton: T): CheckoutSegment<T> {
    return {
        data: cloneSkeleton(skeleton),
        skeleton,
        status: 'loading',
    }
}

export function setSegmentStatus<T>(
    segment: CheckoutSegment<T>,
    status: CheckoutSegmentStatus,
    error?: string
): CheckoutSegment<T> {
    return {
        ...segment,
        status,
        error: status === 'error' ? error : undefined,
    }
}

export function setSegmentLoading<T>(segment: CheckoutSegment<T>): CheckoutSegment<T> {
    return setSegmentStatus(segment, 'loading')
}

/**
 * True while a segment should drive skeleton UI — not yet `ready` or `error`.
 * Matches store finalize error gating (`idle` | `loading`).
 */
export function isCheckoutSegmentPending(status: CheckoutSegmentStatus): boolean {
    return status === 'idle' || status === 'loading'
}

/**
 * Unknown / pre-credits settlement — credit-shaped UI until `checkCredits` resolves.
 * Not a claim the user has note credits; `deriveCheckoutSettlement` is authoritative after entitlements are ready.
 */
export const DEFAULT_CHECKOUT_SETTLEMENT: CheckoutSettlement = 'async_credit'

export function setSegmentReady<T>(segment: CheckoutSegment<T>, data: T): CheckoutSegment<T> {
    return {
        ...segment,
        data,
        status: 'ready',
        error: undefined,
    }
}

export function setSegmentError<T>(
    segment: CheckoutSegment<T>,
    message: string
): CheckoutSegment<T> {
    return setSegmentStatus(segment, 'error', message)
}

/** Reset segment data to skeleton placeholders and idle status. */
export function resetSegment<T>(segment: CheckoutSegment<T>): CheckoutSegment<T> {
    return {
        ...segment,
        data: cloneSkeleton(segment.skeleton),
        status: 'idle',
        error: undefined,
    }
}

/**
 * Derives checkout settlement from journey and entitlement snapshot (see Paid_checkout_flow_sequence_diagram §0).
 */
export function deriveCheckoutSettlement(
    journey: CheckoutJourney | null,
    entitlements: CheckoutEntitlementsData
): CheckoutSettlement {
    if (journey === 'handwriting_stationery' && entitlements.hasStationeryVoucher) {
        return 'voucher'
    }
    if (journey === 'handwritten_note' && entitlements.hasNoteCredit) {
        return 'async_credit'
    }
    return 'paid'
}

/**
 * Picks which checkout UI shell to render from bound store state.
 *
 * Reads `journey` (product track from `resolveCheckoutJourneyBinding`) and `checkoutSettlement`
 * (payment path from `deriveCheckoutSettlement` / `check-for-credits`). Settlement wins for
 * voucher and async credit; journey picks ecommerce vs handwriting product tracks.
 *
 * @see `doc/Paid_checkout_flow_sequence_diagram.md` § Scenario 0
 * @see `doc/guide/GUIDE_handwriting_checkout_api_inventory.md` § Checkout journey and prepare-cart routing
 */
export function resolveCheckoutUiMode(
    journey: CheckoutJourney | null,
    settlement: CheckoutSettlement
): CheckoutUiMode | null {
    if (!journey) {
        return null
    }

    if (journey === 'ecommerce_stationery') {
        return 'ecommerce'
    }

    if (settlement === 'voucher') {
        return 'voucher'
    }

    if (settlement === 'async_credit') {
        return 'credit'
    }

    return 'handwriting'
}

/**
 * Whether to fire optional async prepare-cart when the personalize page loads.
 *
 * Only journeys on {@link CHECKOUT_API_JOURNEYS} pre-warm. `ecommerce_stationery` stays off until
 * Magento/ACCS ecommerce designer cutover to the checkout API (TK-13531) — remove this gate (or
 * add the journey to the allowlist) when prepare-cart supports ecommerce.
 *
 * Signed-in vs guest only affects what `prepareGuestCart` pre-fills (email/addresses from DB) —
 * not whether we call the route for allowlisted journeys.
 */
export function shouldPreWarmOnLoad(journey: CheckoutJourney | null): boolean {
    return journey !== null && isCheckoutApiJourney(journey)
}

/** After check-for-credits, note journey with a fresh credit uses the async_credit shell (no finalize yet). */
export function shouldPivotToAsyncCredit(
    journey: CheckoutJourney | null,
    entitlements: CheckoutEntitlementsData
): boolean {
    return journey === 'handwritten_note' && entitlements.hasNoteCredit
}

/**
 * Note journey submit attempted without payment while live entitlements resolve to paid
 * (e.g. credit revoked on checkout page after async-credit shell was shown).
 */
export function shouldPivotToPaidCheckout(
    journey: CheckoutJourney | null,
    settlement: CheckoutSettlement,
    hasPaymentMethod: boolean
): boolean {
    return journey === 'handwritten_note' && settlement === 'paid' && !hasPaymentMethod
}

/**
 * Whether `submitOrder` calls `POST /api/checkout/place-cart` after finalize.
 *
 * Paid / voucher: sync ACCS place (`placeGuestCart` — Stripe vs `free`).
 * Async credit (after user ack): enqueue note-credit worker via the same place-cart route.
 */
export function shouldRunPlaceOrder(settlement: CheckoutSettlement): boolean {
    return settlement === 'paid' || settlement === 'voucher' || settlement === 'async_credit'
}

/**
 * Note-send journeys keep the recipient's address hidden from checkout (TK-11816) — no client-side
 * recipient address is fetched, so the shipping Address Element must not be shown for these.
 */
export function shouldHideRecipientAddress(journey: CheckoutJourney | null): boolean {
    return journey === 'handwritten_note' || journey === 'handwritten_note_bulk'
}
